import { Label } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import type { ResponsiveLayout, GridColumn } from "../../../shared/schema";
import { isGridColumn } from "../../../shared/schema";
import { i18n } from "@tellescope/i18n";

interface ResponsiveLayoutEditorProps {
  layout?: ResponsiveLayout;
  onChange: (layout: ResponsiveLayout | undefined) => void;
}

// Grid column options with labels
const COLUMN_OPTIONS: { value: GridColumn | "inherit"; label: string }[] = [
  { value: "inherit", label: "Inherit" },
  { value: 12, label: "Full (12/12)" },
  { value: 9, label: "3/4 (9/12)" },
  { value: 8, label: "2/3 (8/12)" },
  { value: 6, label: "Half (6/12)" },
  { value: 4, label: "1/3 (4/12)" },
  { value: 3, label: "1/4 (3/12)" },
];

// Breakpoint labels (xs/mobile is always full-width, not configurable)
const getBreakpoints = (): { key: keyof ResponsiveLayout; label: string; description: string }[] => [
  { key: "sm", label: i18n.t("Tablet", { ns: "layout" }), description: "≥ 640px" },
  { key: "md", label: i18n.t("Laptop", { ns: "layout" }), description: "≥ 768px" },
  { key: "lg", label: i18n.t("Desktop", { ns: "layout" }), description: "≥ 1024px" },
  { key: "xl", label: i18n.t("Wide", { ns: "layout" }), description: "≥ 1280px" },
];

export const ResponsiveLayoutEditor: React.FC<ResponsiveLayoutEditorProps> = ({ layout, onChange }) => {
  const { t } = useTranslation();

  const handleChange = (breakpoint: keyof ResponsiveLayout, value: string) => {
    const currentLayout = layout ?? { xs: 12 };

    if (value === "inherit") {
      const { [breakpoint]: _, ...layoutWithoutBreakpoint } = currentLayout;
      const newLayout = layoutWithoutBreakpoint as ResponsiveLayout;
      // If only xs remains and it's 12, return undefined (default behavior)
      const keys = Object.keys(newLayout) as (keyof ResponsiveLayout)[];
      if (keys.length === 1 && keys[0] === "xs" && newLayout.xs === 12) {
        onChange(undefined);
      } else {
        onChange(newLayout);
      }
    } else {
      const numValue = parseInt(value, 10);
      if (!isGridColumn(numValue)) {
        return;
      }
      const newLayout = { ...currentLayout, [breakpoint]: numValue };
      onChange(newLayout);
    }
  };

  const getValue = (breakpoint: keyof ResponsiveLayout): string => {
    if (!layout) {
      return breakpoint === "xs" ? "12" : "inherit";
    }
    const value = layout[breakpoint];
    return value !== undefined ? String(value) : "inherit";
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-medium">{t("Responsive Width")}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("Configure how wide this block appears at different screen sizes")}
        </p>
      </div>

      <div className="grid grid-cols-5 gap-2">
        <div className="space-y-1">
          <div className="text-center">
            <div className="text-xs font-medium">{t("Mobile")}</div>
            <div className="text-[10px] text-muted-foreground">{"< 640px"}</div>
          </div>
          <div className="h-8 flex items-center justify-center text-xs text-muted-foreground border rounded-md bg-muted/50">
            {t("Full (12/12)")}
          </div>
        </div>
        {getBreakpoints().map(({ key, label, description }) => (
          <div key={key} className="space-y-1">
            <div className="text-center">
              <div className="text-xs font-medium">{label}</div>
              <div className="text-[10px] text-muted-foreground">{description}</div>
            </div>
            <Select value={getValue(key)} onValueChange={(value) => handleChange(key, value)}>
              <Select.Trigger className="h-8 text-xs">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {COLUMN_OPTIONS.map((option) => (
                  <Select.Item key={String(option.value)} value={String(option.value)} className="text-xs">
                    {option.value === "inherit" ? t("Inherit") : option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </div>
        ))}
      </div>

      {/* Visual preview of the layout */}
      <div className="pt-2">
        <div className="text-xs text-muted-foreground mb-1">{t("Preview")}</div>
        <div className="grid grid-cols-12 gap-0.5 h-4">
          {Array.from({ length: 12 }).map((_, i) => {
            const previewValue = layout?.sm ?? 12;
            const isActive = i < previewValue;
            return (
              <div key={i} className={`rounded-sm ${isActive ? "bg-primary" : "bg-muted"}`} title={`Column ${i + 1}`} />
            );
          })}
        </div>
        <div className="text-[10px] text-muted-foreground text-center mt-1">
          {t("Tablet view")}: {layout?.sm ?? 12}/12 {t("columns")}
        </div>
      </div>
    </div>
  );
};
